/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import type { DuplicateMember } from '@/api/counterparties';

/** What the merge screen chooses for an Admin before they choose (`CQ-09`). */

/**
 * Which to keep at first: the one with the most agreements, the oldest of
 * those -- the one already in use.
 */
export function firstKept(
  members: readonly DuplicateMember[],
): DuplicateMember | undefined {
  return members.reduce<DuplicateMember | undefined>(
    (best, member) =>
      !best || member.agreement_count > best.agreement_count ? member : best,
    undefined,
  );
}

/**
 * Which to merge at first: every other one, except a person with an email
 * the kept one does not have. Two people can share a name (`PK-04`), so that
 * one is left for the Admin to tick.
 */
export function firstMerged(
  members: readonly DuplicateMember[],
  kept: DuplicateMember | undefined,
): Set<string> {
  return new Set(
    members
      .filter((member) => member.id !== kept?.id)
      .filter(
        (member) =>
          !(
            member.kind === 'person' &&
            member.email &&
            kept?.email &&
            member.email.toLowerCase() !== kept.email.toLowerCase()
          ),
      )
      .map((member) => member.id),
  );
}

/** What the merge screen needs of a counterparty, from either list. */
export interface Mergeable {
  id: string;
  name: string;
  kind: 'person' | 'organization';
  agreement_count: number;
}

/** "Acme Ltd", "Acme Ltd and Acme Co", "Acme Ltd, Acme Co and Acme Group". */
export function namesInWords(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;
}

/**
 * Whether these can be merged together: a person is never merged into an
 * organization, nor an organization into a person. The server decides again.
 */
export function oneKind(picked: readonly Mergeable[]): boolean {
  return new Set(picked.map((counterparty) => counterparty.kind)).size <= 1;
}

/**
 * What a merge will do, before it is done: "3 agreements will move to Acme
 * Inc; Acme Incorporated will be switched off."
 */
export function mergeSummary(kept: Mergeable, merging: readonly Mergeable[]): string {
  const moving = merging.reduce(
    (sum, counterparty) => sum + counterparty.agreement_count,
    0,
  );
  const agreements =
    moving === 0
      ? 'No agreements'
      : `${String(moving)} ${moving === 1 ? 'agreement' : 'agreements'}`;
  const names = namesInWords(merging.map((counterparty) => counterparty.name));
  return `${agreements} will move to ${kept.name}; ${names} will be switched off.`;
}

/*
 * What a counterparty's name may be (VL-2044) -- the rule the server holds
 * too, in `core/counterparties/names.py`. Letters in any language, numbers,
 * spaces and . , ' - & ( ) / only, with at least one letter or number. The
 * server decides; this says so on the field before Create is pressed.
 */

/** Said of a name with a character no name has: "@", "<b>", "John!". */
export const NOT_A_NAME = "Use letters, numbers, spaces and . , ' - & ( ) / only.";
/** Said of a name with nothing in it but spaces and punctuation: "---". */
export const NO_LETTER = 'Enter a name with at least one letter or number.';

const SPACES = /\s+/gu;
const APOSTROPHES = /[‘’ʼʹ`´′]/gu;
const ALLOWED = /^[\p{L}\p{N}\p{M} .,'\-&()/]*$/u;
const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;

/** The name as it is saved: no spaces around it, one between words, one apostrophe. */
export function tidyName(raw: string): string {
  return raw.replace(SPACES, ' ').trim().replace(APOSTROPHES, "'");
}

/** Why this is not a name, or `null` where it is one. Empty is the caller's to say. */
export function nameProblem(raw: string): string | null {
  const name = tidyName(raw);
  if (name === '') return null;
  if (!ALLOWED.test(name)) return NOT_A_NAME;
  if (!LETTER_OR_DIGIT.test(name)) return NO_LETTER;
  return null;
}
